import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';

export interface QuestPayload {
  quest: {
    learningDay: string;
    key: string;
    area: string;
    status: string;
    note: string | null;
    title: string;
    task: string;
    forGrownUps: string;
  } | null;
}

/**
 * Today's real-world quest.
 *
 * The screen's job is to say one thing and then get out of the way. Everything
 * that matters here happens in a kitchen or on a footpath, where no software
 * can see it - so there is no timer, no verification, no streak, and nothing is
 * taken away for a quest that never gets done.
 *
 * "I did it" is taken at face value. An app that treated a six-year-old's word
 * as evidence to be checked would poison the habit it exists to build.
 */
export function Quest() {
  const navigate = useNavigate();
  const session = useSession();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const today = useQuery({
    queryKey: ['quest', 'today'],
    queryFn: () => api<QuestPayload>('/child/quest'),
  });

  const markDone = useMutation({
    mutationFn: () =>
      api('/child/quest/done', {
        method: 'POST',
        body: { note: note.trim() || undefined },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quest', 'today'] }),
  });

  if (session.isPending) return <main className="screen">Just a moment&hellip;</main>;
  if (!session.data?.child) return <Navigate to="/" replace />;

  const quest = today.data?.quest ?? null;
  const done = quest?.status === 'done';

  return (
    <main className="screen quest">
      <span className="pill">Out in the real world</span>

      {today.isPending && <p>Finding today&rsquo;s quest&hellip;</p>}

      {today.data && !quest && (
        <>
          <h1>No quest today</h1>
          <p>Have a go at an adventure instead.</p>
        </>
      )}

      {quest && (
        <>
          <h1>{quest.title}</h1>
          <p className="quest-task">{quest.task}</p>

          {done ? (
            <>
              <p className="praise-note">You did it. Nice one.</p>
              {quest.note && <blockquote className="child-words">{quest.note}</blockquote>}
            </>
          ) : (
            <>
              <label className="checkin-note">
                How did it go? You don&rsquo;t have to write anything.
                <textarea
                  rows={4}
                  value={note}
                  placeholder="Tell us about it, or leave it empty."
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>

              <button
                type="button"
                className="btn btn--big"
                disabled={markDone.isPending}
                onClick={() => markDone.mutate()}
              >
                {markDone.isPending ? 'Saving…' : 'I did it!'}
              </button>

              {/* No "I did not do it" button. Nothing happens either way, and
                  asking a child to record a failure serves nobody. */}
              <p className="muted">There&rsquo;s no rush. It&rsquo;ll still be here later.</p>
            </>
          )}
        </>
      )}

      <button type="button" className="btn btn--quiet" onClick={() => navigate('/home')}>
        Back home
      </button>
    </main>
  );
}
